/**
 * Norwegian (bokmål) UI labels for every domain enum.
 * All user-facing text lives here so wording stays consistent across web,
 * notifications and agent-written summaries.
 */
import type {
  AgentKey,
  AgentRunStatus,
  BrandScope,
  DiscoveredSourceStatus,
  ProfileDocumentKind,
  ProfileDocumentType,
  AgentRunTrigger,
  AreaType,
  ChannelTargetStatus,
  ChannelType,
  Company,
  ContentFormat,
  ContentFormatGroup,
  ContentStatus,
  KpiSource,
  NotificationChannel,
  NotificationType,
  ModuleKey,
  ModuleRole,
  Pillar,
  PublishMode,
  TenderBuyerType,
  TenderNoBidReason,
  TenderNoticeType,
  TenderStatus,
  AssetImportEntity,
  AssignmentSource,
  BillingPeriod,
  EquipmentCategory,
  EquipmentStatus,
  NeedStatus,
  NeedType,
  ReportStatus,
  RequirementScope,
  SoftwareCategory,
  UseStatus,
  UserRole,
} from './enums.js';
import type {
  HoldingConfidence,
  InteractionType,
  OrganizationType,
  ProductHoldingStatus,
} from './enums.js';
import type { AssessmentStatus, PriorityFactorKey, PriorityTier } from './enums.js';
import type {
  SocialAccountKind,
  SocialHealthFlag,
  SocialMetricSource,
  SocialPlatform,
} from './enums.js';
import type { RhythmStatus } from './dto.js';
import type { ContentGate } from './status-flow.js';

export const USER_ROLE_LABELS: Record<UserRole, string> = {
  ADMIN: 'Administrator',
  MEMBER: 'Produktansvarlig',
  VIEWER: 'Leser',
};

export const MODULE_KEY_LABELS: Record<ModuleKey, string> = {
  MARKETING: 'Marked',
  SALES: 'Salg',
  ASSETS: 'Utstyr og lisenser',
};

/** Short one-liners for the module cards in admin. */
export const MODULE_KEY_HELP: Record<ModuleKey, string> = {
  MARKETING: 'Dagens Nova: redaksjonsplan, agenter og publisering.',
  SALES: 'Anbudsradar, marked og kunder, salgsmål.',
  ASSETS: 'Register over lisenser og utstyr, bruk og behov.',
};

export const MODULE_ROLE_LABELS: Record<ModuleRole, string> = {
  VIEWER: 'Leser',
  MEMBER: 'Bidragsyter',
  ADMIN: 'Administrator',
};

export const AREA_TYPE_LABELS: Record<AreaType, string> = {
  PRODUCT: 'Produktområde',
  COMPANY_PROFILE: 'Selskapsprofil',
  EMPLOYEE_PROFILE: 'Ansattprofil',
};

/** Rhythm states for the rhythm view (07 — 2.2.1). */
export const RHYTHM_STATUS_LABELS: Record<RhythmStatus, string> = {
  ON_TRACK: 'I rytme',
  SLIPPING: 'På etterskudd',
  QUIET: 'Stille',
};

export const COMPANY_LABELS: Record<Company, string> = {
  SPAMA: 'Spama',
  PROKOM: 'Prokom',
  BEGGE: 'Begge selskaper',
};

export const PILLAR_LABELS: Record<Pillar, string> = {
  PRAKSIS: 'Praksis',
  FAG: 'Fag',
  PRODUKT: 'Produkt',
  BRANSJE: 'Bransje',
};

export const PILLAR_DESCRIPTIONS: Record<Pillar, string> = {
  PRAKSIS: 'Kundecase og erfaringer fra virkeligheten',
  FAG: 'Fagartikler, lovverk og beste praksis',
  PRODUKT: 'Produktnyheter, slipp og demoer',
  BRANSJE: 'Trender, undersøkelser og debatt',
};

export const CONTENT_FORMAT_LABELS: Record<ContentFormat, string> = {
  ARTIKKEL: 'Artikkel',
  KUNDECASE: 'Kundecase',
  LINKEDIN_POST: 'SoMe-post',
  NYHETSBREV: 'Nyhetsbrev',
  WEBINAR: 'Webinar',
  ARRANGEMENT: 'Arrangement',
  VIDEO: 'Video',
  ANNET: 'Annet',
  PRODUKTARK: 'Produktark',
  PLAGG: 'Plagg',
  MERCH: 'Merch',
  ANNONSE: 'Annonse',
};

/** Norwegian headings for the two format groups in the format picker. */
export const CONTENT_FORMAT_GROUP_LABELS: Record<ContentFormatGroup, string> = {
  DIGITALT: 'Digitalt',
  TRYKK_FYSISK: 'Trykk/fysisk',
};

/**
 * The short names, for chips, columns and anywhere else that is tight.
 *
 * «Bestilt» rather than «Godkjent»: approving happens twice in the flow — once
 * to the idea, once to the text — and using the same word for both is what
 * confused people (dokumentasjon/07-utviklingsplan.md 2.1.4).
 */
export const CONTENT_STATUS_LABELS: Record<ContentStatus, string> = {
  PROPOSED: 'Pre-forslag',
  SUGGESTED: 'Forslag',
  APPROVED: 'Til produksjon',
  DRAFT: 'I arbeid',
  SCHEDULED: 'Planlagt',
  PUBLISHED: 'Publisert',
  REJECTED: 'Avvist',
  CANCELLED: 'Kansellert',
  EXPIRED: 'Utgått',
};

/** The unambiguous names, for where there is room to spell it out. */
export const CONTENT_STATUS_LONG_LABELS: Record<ContentStatus, string> = {
  ...CONTENT_STATUS_LABELS,
};

/** What a status means. Who does the next step is stated separately. */
export const CONTENT_STATUS_HELP: Record<ContentStatus, string> = {
  PROPOSED:
    'Et pre-forslag fra Nova som venter på gjennomgang. Vises bare i forslagsveiviseren — ikke på tavla eller i listene — til den ansvarlige legger det til eller forkaster det.',
  SUGGESTED: 'En idé, fra Nova eller et menneske. Ingen har bestemt noe ennå.',
  APPROVED: 'Klarert — innholdet skal lages.',
  DRAFT: 'Innholdet er under arbeid.',
  SCHEDULED: 'Alt er klart til publisering.',
  PUBLISHED: 'Bekreftet ute.',
  REJECTED: 'Vi skal ikke lage dette. Begrunnelsen står i loggen.',
  CANCELLED: 'Stoppet underveis. Begrunnelsen står i loggen.',
  EXPIRED: 'Forslag som ikke lenger er aktuelle — aktualitetsvinduet er passert.',
};

/** Shown when a move is blocked because the draft is not there yet. */
export const CONTENT_GATE_MESSAGES: Record<ContentGate, string> = {
  NEEDS_DRAFT: 'Det finnes ikke noe utkast ennå. Skriv et utkast selv, eller be Nova skrive ett.',
  NEEDS_CONTENT: 'Innholdet er tomt. Skriv utkastet ferdig før du planlegger publisering.',
};

/** Derived, not a stored status — shown when a deadline has passed. */
export const OVERDUE_LABEL = 'Forsinket';

export const CHANNEL_TYPE_LABELS: Record<ChannelType, string> = {
  WEBSITE_AIMCMS: 'Nettside',
  LINKEDIN_ORG: 'LinkedIn (selskap)',
  LINKEDIN_EMPLOYEE: 'LinkedIn (ansatt)',
  NEWSLETTER_MAKE: 'Nyhetsbrev',
  WEBINAR: 'Webinar',
  TRYKK: 'Trykk',
  OTHER: 'Annet',
};

export const PUBLISH_MODE_LABELS: Record<PublishMode, string> = {
  API: 'Automatisk via API',
  MANUAL_PREPARED: 'Klargjort — publiseres manuelt',
};

export const CHANNEL_TARGET_STATUS_LABELS: Record<ChannelTargetStatus, string> = {
  PENDING: 'Venter',
  PREPARED: 'Klargjort',
  PUBLISHED_CONFIRMED: 'Publisering bekreftet',
};

export const AGENT_LABELS: Record<AgentKey, string> = {
  director: 'Markedsdirektøren',
  planner: 'Redaksjonsplanleggeren',
  scout: 'Omverdensovervåkeren',
  writer: 'Innholdsprodusenten',
  reminder: 'Purreagenten',
  analyst: 'Analytikeren',
  digest: 'Oppsummeringsagenten',
  'tender-scout': 'Anbudsspeideren',
};

export const AGENT_RUN_STATUS_LABELS: Record<AgentRunStatus, string> = {
  RUNNING: 'Kjører',
  SUCCESS: 'Fullført',
  FAILED: 'Feilet',
  PARTIAL: 'Delvis fullført',
};

export const AGENT_RUN_TRIGGER_LABELS: Record<AgentRunTrigger, string> = {
  SCHEDULE: 'Tidsplan',
  MANUAL: 'Startet manuelt',
  DIRECTOR: 'Bestilt av direktøren',
};

export const NOTIFICATION_TYPE_LABELS: Record<NotificationType, string> = {
  REMINDER: 'Påminnelse',
  ESCALATION: 'Eskalering',
  INFO: 'Informasjon',
  PREPARED_POST: 'Klar til publisering',
  COMMENT: 'Ny kommentar',
};

export const NOTIFICATION_CHANNEL_LABELS: Record<NotificationChannel, string> = {
  TEAMS: 'Teams',
  EMAIL: 'E-post',
  INAPP: 'I Nova',
};

export const KPI_SOURCE_LABELS: Record<KpiSource, string> = {
  MANUAL: 'Manuell registrering',
  MAKE: 'Make.as',
  LINKEDIN: 'LinkedIn',
  INTERNAL: 'Beregnet av Nova',
};

export const BRAND_SCOPE_LABELS: Record<BrandScope, string> = {
  GLOBAL: 'Felles',
  COMPANY: 'Selskap',
  AREA: 'Område',
  CHANNEL: 'Kanal',
};

export const PROFILE_DOCUMENT_TYPE_LABELS: Record<ProfileDocumentType, string> = {
  ENDRINGSLOGG: 'Endringslogg',
  VEIKART: 'Veikart',
  PRODUKTARK: 'Produktark',
  MERKEVAREMANUAL: 'Merkevaremanual',
  ANNET: 'Annet',
};

export const PROFILE_DOCUMENT_KIND_LABELS: Record<ProfileDocumentKind, string> = {
  LINK: 'Lenke',
  FILE: 'Opplastet fil',
  INLINE: 'Skrevet i Nova',
};

/** What Nova can actually do with each kind, said plainly. */
export const PROFILE_DOCUMENT_KIND_HELP: Record<ProfileDocumentKind, string> = {
  LINK: 'Peker til SharePoint, Teams eller en nettadresse. Nova kan ikke lese innholdet — agentene får tittel, type og notat.',
  FILE: 'Lastet opp til Nova. Tekstfiler (.md, .txt) kan agentene lese; andre formater kan bare lastes ned.',
  INLINE: 'Skrevet rett i Nova. Agentene leser hele teksten hver kjøring.',
};

/** Status for a source the scout found on its own (07 — kildestyring). */
export const DISCOVERED_SOURCE_STATUS_LABELS: Record<DiscoveredSourceStatus, string> = {
  SUGGESTED: 'Forslag',
  PROMOTED: 'Lagt til i listen',
  DISMISSED: 'Avvist',
};

// ── Salg / Anbudsradar ───────────────────────────────────────────────────────

export const TENDER_NOTICE_TYPE_LABELS: Record<TenderNoticeType, string> = {
  KONKURRANSE: 'Konkurranse',
  FORHANDSKUNNGJORING: 'Forhåndskunngjøring',
  TILDELING: 'Tildeling',
};

export const TENDER_STATUS_LABELS: Record<TenderStatus, string> = {
  NY: 'Ny',
  VURDERES: 'Vurderes',
  BID: 'Bid',
  NO_BID: 'No-bid',
  UNDER_ARBEID: 'Under arbeid',
  LEVERT: 'Levert',
  VUNNET: 'Vunnet',
  TAPT: 'Tapt',
  AVLYST: 'Avlyst',
};

/** What each status means, said plainly — for tooltips and the detail page. */
export const TENDER_STATUS_HELP: Record<TenderStatus, string> = {
  NY: 'Nytt treff fra Doffin. Ingen har vurdert det ennå.',
  VURDERES: 'Under vurdering — selger ser på om vi skal gi tilbud.',
  BID: 'Vi går for det. Tilbud skal utarbeides.',
  NO_BID: 'Vi gir ikke tilbud. Årsaken er registrert som læringsdata.',
  UNDER_ARBEID: 'Tilbudet er under utarbeidelse.',
  LEVERT: 'Tilbudet er levert. Venter på resultat.',
  VUNNET: 'Vi vant kontrakten.',
  TAPT: 'Vi tapte konkurransen.',
  AVLYST: 'Konkurransen ble avlyst av oppdragsgiver.',
};

export const TENDER_NO_BID_REASON_LABELS: Record<TenderNoBidReason, string> = {
  KAPASITET: 'Manglende kapasitet',
  KRAV_IKKE_OPPFYLT: 'Krav vi ikke møter',
  PRIS: 'Pris/lønnsomhet',
  FEIL_PRODUKT: 'Feil produkt/utenfor tilbud',
  STRATEGISK: 'Strategisk nedprioritert',
  ANNET: 'Annet',
};

export const TENDER_BUYER_TYPE_LABELS: Record<TenderBuyerType, string> = {
  KOMMUNE: 'Kommune',
  FYLKESKOMMUNE: 'Fylkeskommune',
  STAT: 'Stat',
  HELSE: 'Helseforetak',
  ANNET: 'Annet',
};

// ── Utstyr og lisenser (ASSETS module) — Norwegian labels ────────────────────

export const SOFTWARE_CATEGORY_LABELS: Record<SoftwareCategory, string> = {
  PRODUCTIVITY: 'Produktivitet',
  AI: 'KI-verktøy',
  DESIGN: 'Design',
  DEV: 'Utvikling',
  COMMUNICATION: 'Kommunikasjon',
  FINANCE: 'Økonomi',
  OTHER: 'Annet',
};

export const BILLING_PERIOD_LABELS: Record<BillingPeriod, string> = {
  MONTHLY: 'Per måned',
  YEARLY: 'Per år',
};

export const REQUIREMENT_SCOPE_LABELS: Record<RequirementScope, string> = {
  ALL_EMPLOYEES: 'Alle ansatte',
  GROUP: 'En gruppe',
};

export const ASSIGNMENT_SOURCE_LABELS: Record<AssignmentSource, string> = {
  MANUAL: 'Lagt inn manuelt',
  IMPORT: 'Importert',
  GRAPH: 'Hentet automatisk',
  SELF_REPORTED: 'Meldt inn selv',
};

export const USE_STATUS_LABELS: Record<UseStatus, string> = {
  IN_USE: 'I bruk',
  NOT_IN_USE: 'Ikke i bruk',
  ENDED: 'Avsluttet',
};

export const EQUIPMENT_CATEGORY_LABELS: Record<EquipmentCategory, string> = {
  PC: 'PC',
  MOBILE: 'Mobil',
  TABLET: 'Nettbrett',
  MONITOR: 'Skjerm',
  HEADSET: 'Headset',
  DOCK: 'Dokk',
  KEYBOARD_MOUSE: 'Tastatur og mus',
  OTHER: 'Annet',
};

export const EQUIPMENT_STATUS_LABELS: Record<EquipmentStatus, string> = {
  IN_USE: 'I bruk',
  NOT_IN_USE: 'Ikke i bruk',
  RETURNED: 'Levert tilbake',
  LOST: 'Mistet',
  BROKEN: 'Ødelagt',
};

export const REPORT_STATUS_LABELS: Record<ReportStatus, string> = {
  OPEN: 'Åpen',
  WITHDRAWN: 'Trukket tilbake',
  RESOLVED: 'Løst',
};

export const NEED_TYPE_LABELS: Record<NeedType, string> = {
  SOFTWARE: 'Programvare',
  EQUIPMENT: 'Utstyr',
  OTHER: 'Annet',
};

export const NEED_STATUS_LABELS: Record<NeedStatus, string> = {
  NEW: 'Ny',
  IN_REVIEW: 'Under behandling',
  APPROVED: 'Godkjent',
  REJECTED: 'Avslått',
  ORDERED: 'Bestilt',
  DONE: 'Levert',
};

export const ASSET_IMPORT_ENTITY_LABELS: Record<AssetImportEntity, string> = {
  LICENCE: 'Lisenser',
  EQUIPMENT: 'Utstyr',
};

// ── Salg / Marked og kunder (SALES-modulen, fase S2) ─────────────────────────

export const ORGANIZATION_TYPE_LABELS: Record<OrganizationType, string> = {
  KOMMUNE: 'Kommune',
  FYLKESKOMMUNE: 'Fylkeskommune',
  STAT: 'Statlig virksomhet',
  HELSE: 'Helseforetak',
  BANK: 'Bank',
  PRIVAT: 'Privat',
  ANNET: 'Annet',
};

export const PRODUCT_HOLDING_STATUS_LABELS: Record<ProductHoldingStatus, string> = {
  KUNDE: 'Kunde',
  TIDLIGERE_KUNDE: 'Tidligere kunde',
  PROSPEKT: 'Prospekt',
  KONKURRENT_KUNDE: 'Konkurrentens kunde',
  IKKE_RELEVANT: 'Ikke relevant',
};

/** Kort forklaring av hver status — for tooltips og detaljvisning. */
export const PRODUCT_HOLDING_STATUS_HELP: Record<ProductHoldingStatus, string> = {
  KUNDE: 'De bruker produktet vårt i dag.',
  TIDLIGERE_KUNDE: 'De har vært kunde, men er det ikke lenger.',
  PROSPEKT: 'Aktuell målgruppe — bruker en annen løsning eller ingen i dag.',
  KONKURRENT_KUNDE: 'De er kunde hos en konkurrent på dette området.',
  IKKE_RELEVANT: 'Ikke aktuelt marked for dette produktet.',
};

export const HOLDING_CONFIDENCE_LABELS: Record<HoldingConfidence, string> = {
  HOY: 'Høy konfidens',
  MIDDELS: 'Middels konfidens',
  LAV: 'Lav konfidens',
};

export const INTERACTION_TYPE_LABELS: Record<InteractionType, string> = {
  MOTE: 'Møte',
  SAMTALE: 'Samtale',
  EPOST: 'E-post',
  DEMO: 'Demo',
  TILBUD: 'Tilbud',
  ANNET: 'Annet',
};

// ── Salg / Prioritering + situasjonskartlegging (SALES-modulen, fase S3) ──────

export const PRIORITY_TIER_LABELS: Record<PriorityTier, string> = {
  A: 'Tier A',
  B: 'Tier B',
  C: 'Tier C',
};

/** Kort forklaring av hver tier — for tooltips og overstyringsdialogen. */
export const PRIORITY_TIER_HELP: Record<PriorityTier, string> = {
  A: 'Høyeste prioritet — ring/følg opp først.',
  B: 'Middels prioritet — hold varm og følg med.',
  C: 'Lav prioritet akkurat nå.',
};

export const PRIORITY_FACTOR_LABELS: Record<PriorityFactorKey, string> = {
  contractExpiry: 'Kontraktsutløp',
  size: 'Størrelse',
  fit: 'Passform',
  relationship: 'Relasjon',
  signals: 'Signaler',
};

/** Hva hver faktor måler — vises i vekteditoren og faktorbrytningen. */
export const PRIORITY_FACTOR_HELP: Record<PriorityFactorKey, string> = {
  contractExpiry: 'Nærmere kontraktsutløp gir høyere score.',
  size: 'Folketall og/eller årlig avtaleverdi.',
  fit: 'Passform fra siste bekreftede kartlegging.',
  relationship: 'Ferskhet og volum av oppfølginger.',
  signals: 'Doffin-tildelinger som matcher org.nr, og nyheter når tilgjengelig.',
};

export const ASSESSMENT_STATUS_LABELS: Record<AssessmentStatus, string> = {
  AGENT: 'Agentvurdering',
  CONFIRMED: 'Bekreftet',
};

// ── SoMe-overvåkning (Marked) ────────────────────────────────────────────────

export const SOCIAL_PLATFORM_LABELS: Record<SocialPlatform, string> = {
  LINKEDIN: 'LinkedIn',
  FACEBOOK: 'Facebook',
};

export const SOCIAL_ACCOUNT_KIND_LABELS: Record<SocialAccountKind, string> = {
  COMPANY: 'Selskapsside',
  EMPLOYEE: 'Ansattprofil',
  PAGE: 'Side',
  OTHER: 'Annet',
};

export const SOCIAL_METRIC_SOURCE_LABELS: Record<SocialMetricSource, string> = {
  MANUAL: 'Lagt inn manuelt',
  CHROME: 'Lest i nettleseren',
  INTERNAL: 'Hentet automatisk',
};

export const SOCIAL_HEALTH_FLAG_LABELS: Record<SocialHealthFlag, string> = {
  VOKSER: 'Vokser',
  FLAT: 'Flat',
  STILLE: 'Stille',
  INAKTIV: 'Inaktiv',
};

/** Kort forklaring av hvert helseflagg — for tooltips og overvåkningskortene. */
export const SOCIAL_HEALTH_FLAG_HELP: Record<SocialHealthFlag, string> = {
  VOKSER: 'Følgertallet stiger fra forrige måned.',
  FLAT: 'Følgertallet står omtrent stille fra forrige måned.',
  STILLE: 'Ingen innlegg i siste registrerte måned.',
  INAKTIV: 'Mangler tall for forrige og inneværende måned — ikke fulgt opp.',
};
